import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { HubspotFormPage, QueryHubspotIntegrationFormsArgs } from 'gql/graphql';
import { gql } from 'graphql-request';
import { TGenericError } from 'types/TGenericError';
import clientUtil from 'utils/clientUtil';

export interface HubSpotFormField {
  objectTypeId: string;
  name: string;
  label: string;
  required: boolean;
  hidden: boolean;
  fieldType: string;
  validation?: {
    blockedEmailDomains?: string[];
    useDefaultBlockList?: boolean;
  };
}

export interface HubSpotFormFieldGroup {
  groupType: string;
  richTextType: string;
  fields: HubSpotFormField[];
}

export interface HubSpotFormConfiguration {
  language: string;
  cloneable: boolean;
  postSubmitAction: {
    type: string;
    value: string;
  };
  editable: boolean;
  archivable: boolean;
  recaptchaEnabled: boolean;
  notifyContactOwner: boolean;
  notifyRecipients: string[];
  createNewContactForNewEmail: boolean;
  prePopulateKnownValues: boolean;
  allowLinkToResetKnownValues: boolean;
  lifecycleStages: string[];
}

export interface HubSpotFormDisplayOptions {
  renderRawHtml: boolean;
  theme: string;
  submitButtonText: string;
  style: {
    fontFamily: string;
    backgroundWidth: string;
    labelTextColor: string;
    labelTextSize: string;
    helpTextColor: string;
    helpTextSize: string;
    legalConsentTextColor: string;
    legalConsentTextSize: string;
    submitColor: string;
    submitAlignment: string;
    submitFontColor: string;
    submitSize: string;
  };
  cssClass: string | null;
}

export interface HubSpotMarketingForm {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  archived: boolean;
  fieldGroups: HubSpotFormFieldGroup[];
  configuration: HubSpotFormConfiguration;
  displayOptions: HubSpotFormDisplayOptions;
  legalConsentOptions: {
    type: string;
  };
  formType: string;
}

export type HubSpotMarketingFormAggregated = HubSpotMarketingForm & {
  language?: string;
  name?: string;
  publishDate?: string;
};

type TVariables = QueryHubspotIntegrationFormsArgs;

type TData = HubSpotMarketingForm;

export const useHubSpotIntegrationGetFormMutation = (
  options: Omit<UseMutationOptions<TData, TGenericError, TVariables, unknown>, 'mutationFn'> = {}
) => {
  const client = clientUtil();

  const graphql = gql`
    query hubspotIntegrationForms($formId: ID) {
      hubspotIntegrationForms(formId: $formId) {
        nodes {
          formId
          jsonContent
          language
          name
          publishDate
        }
        hasNextPage
        nodesCount
        pagesCount
      }
    }
  `;

  return useMutation<TData, TGenericError, TVariables, unknown>({
    mutationFn: async (variables: TVariables): Promise<TData> =>
      (
        (await client.request({
          data: {
            gql: graphql,
            variables
          }
        })) as HubspotFormPage
      ).nodes
        ?.map((item) => JSON.parse(item.jsonContent))
        ?.at(0),
    ...options
  });
};
